import { UserSummary } from '@beeboard/common';

export function getUserCaption(user: UserSummary) {
  if (user.first_name && user.last_name) {
    return user.first_name + ' ' + user.last_name;
  }

  return user.username;
}

/**
 * Two-letter initials from a display name (first + last word), used as the
 * fallback when a user has no avatar image.
 */
export function getInitialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';

  return (first + last).toUpperCase() || '?';
}

export function isDeletedUser(user: UserSummary | null | undefined): boolean {
  return user?.user_type === 'DELETED';
}

/**
 * Caption for a user, with the neutral placeholder for deleted accounts. A
 * deleted user still shows up on the work they completed, so they need a
 * caption of their own rather than a blank or a stale name.
 */
export function getUserDisplayCaption(user: UserSummary): string {
  return isDeletedUser(user) ? $t('users.deletedUser') : getUserCaption(user);
}

/** Initials disc content; deleted users get a neutral "?" instead of "DU". */
export function getUserDisplayInitials(user: UserSummary): string {
  return isDeletedUser(user) ? '?' : getInitialsFromName(getUserCaption(user));
}

/** Whether an avatar image should be fetched/shown for this user. */
export function hasDisplayableAvatar(user: UserSummary): boolean {
  return !isDeletedUser(user) && !!user.has_profile_picture;
}

/**
 * The user to *display* as responsible for an item: once it is done, whoever
 * completed it (snapshotted server-side into `completion_user`) rather than
 * whoever currently holds its function.
 *
 * An item stores only its `responsible.function_id` and the person is resolved
 * through that function at render time, so handing a function to a different
 * holder would otherwise rewrite who appears to have done every completed item
 * pointing at it. Only the person swaps — the function is untouched.
 *
 * Falls back to the current assignee when the snapshot is missing (legacy rows,
 * or nobody assigned at completion time).
 */
export function effectiveResponsibleUser<T>(
  isDone: boolean,
  completionUser: UserSummary | null | undefined,
  responsibleUser: T,
): T | UserSummary {
  return isDone && completionUser ? completionUser : responsibleUser;
}

/**
 * An item whose completion can be resolved: store models carry `completionUser`,
 * raw DTOs carry `completion_user`. Both spellings are accepted so call sites
 * can hand over whichever shape they already hold.
 */
export interface CompletableItem {
  state?: string | null;
  completionUser?: UserSummary | null;
  completion_user?: UserSummary | null;
}

/**
 * Item-taking form of {@link effectiveResponsibleUser}: the caller passes the
 * item plus its resolved current assignee, instead of repeating the
 * `state === 'done'` / `completionUser` pair at every call site (and risking an
 * argument-order slip in a boolean-first positional call).
 */
export function effectiveResponsibleUserOf<T>(
  item: CompletableItem | null | undefined,
  responsibleUser: T,
): T | UserSummary {
  return effectiveResponsibleUser(
    item?.state === 'done',
    item?.completionUser ?? item?.completion_user,
    responsibleUser,
  );
}
